import { Loader2 } from 'lucide-react';
import {
  useAgeDistributionQuery,
  useCampaignAnalyticsQuery,
  type AgeDistributionItem,
} from '@/features/campaigns/api/campaignDetailApi';
import { AGE_COLORS } from '../campaignDetailPage.constants';
import { Empty, Loading } from '../CampaignDetailPagePrimitives';
import { normalizeVehicleDistribution } from '../campaignDetailPage.utils';

function DonutRing({
  segments,
  centerLabel,
  centerValue,
  size = 140,
  className = 'mx-auto',
}: {
  segments: Array<{ pct: number; color: string }>;
  centerLabel: string;
  centerValue: string | number;
  size?: number;
  className?: string;
}) {
  let offset = 0;
  const hasData = segments.some((s) => s.pct > 0);
  return (
    <div className={`relative ${className}`} style={{ width: size, height: size }}>
      <svg viewBox="0 0 36 36" className="h-full w-full -rotate-90">
        <circle cx="18" cy="18" r="15.9" fill="none" stroke="#F1F5F9" strokeWidth="2.8" />
        {hasData &&
          segments.map((seg, i) => {
            const el = (
              <circle
                key={i}
                cx="18" cy="18" r="15.9" fill="none" stroke={seg.color} strokeWidth="2.8"
                strokeDasharray={`${seg.pct} ${100 - seg.pct}`}
                strokeDashoffset={`${-offset}`}
                strokeLinecap="round"
                className="transition-all duration-700"
              />
            );
            offset += seg.pct;
            return el;
          })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold text-[var(--dash-ink)]">{centerValue}</span>
        <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-widest text-[var(--dash-muted)]">{centerLabel}</span>
      </div>
    </div>
  );
}

export function AnalyticsTab({ campaignId }: { campaignId?: string }) {
  const q = useCampaignAnalyticsQuery(campaignId);
  const ageQ = useAgeDistributionQuery(campaignId);
  const d = q.data?.data;
  const ageData: AgeDistributionItem[] =
    ageQ.data?.data || (Array.isArray(ageQ.data) ? (ageQ.data as AgeDistributionItem[]) : []);
  const ageTotal = ageData.reduce((sum, item) => sum + (item.count || 0), 0);

  if (q.isPending) return <Loading />;
  if (!d) return <Empty text="No analytics data available yet." />;

  const vehicles = normalizeVehicleDistribution(d);
  const vehicleTotal = vehicles.reduce((sum, item) => sum + item.count, 0);

  const maleCount = d.maleCount || 0;
  const femaleCount = d.femaleCount || 0;
  const totalGender = maleCount + femaleCount;
  const peopleCount = d.peopleCount ?? d.audienceCount ?? totalGender;
  const audienceItems = [
    { key: 'people', label: 'People', count: peopleCount, color: '#8B5CF6' },
  ];
  const malePct = totalGender > 0 ? (maleCount / totalGender) * 100 : 0;
  const femalePct = totalGender > 0 ? (femaleCount / totalGender) * 100 : 0;
  const engScore = d.engagementScore ?? 0;
  const engPct = Math.min(engScore, 100);
  const footfallVal = Number(d.footfallCount) || 0;
  const footfallPct = footfallVal > 0 ? 75 : 0;

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        <div className="rounded-2xl border border-[var(--dash-line)] bg-white p-6 shadow-sm">
          <h3 className="m-0 mb-5 text-sm font-bold text-[var(--dash-ink)]">Gender Distribution</h3>
          <DonutRing
            segments={[
              { pct: malePct, color: '#2DD4BF' },
              { pct: femalePct, color: '#FB7185' },
            ]}
            centerValue={totalGender}
            centerLabel="Total"
          />
          <div className="mt-5 flex items-center justify-center gap-8">
            {[
              { label: 'Male', count: maleCount, color: '#2DD4BF' },
              { label: 'Female', count: femaleCount, color: '#FB7185' },
            ].map((g) => (
              <div key={g.label} className="flex items-center gap-2 text-sm">
                <span className="h-2.5 w-2.5 rounded" style={{ backgroundColor: g.color }} />
                <span className="text-[var(--dash-muted)]">{g.label}</span>
                <span className="font-bold text-[var(--dash-ink)]">{g.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col rounded-2xl border border-[var(--dash-line)] bg-white p-6 shadow-sm">
          <h3 className="m-0 mb-1 text-sm font-bold text-[var(--dash-ink)]">Dwell Time</h3>
          <p className="m-0 text-xs text-[var(--dash-muted)]">Average duration</p>
          <div className="flex flex-1 flex-col items-center justify-center">
            <span className="text-5xl font-extrabold tracking-tight text-emerald-600">
              {d.dwellTime != null ? Number(d.dwellTime).toFixed(2) : '0.00'}
            </span>
            <span className="mt-1 text-xs font-semibold uppercase tracking-wider text-[var(--dash-muted)]">sec</span>
          </div>
        </div>

        <div className="rounded-2xl border border-[var(--dash-line)] bg-white p-6 shadow-sm">
          <h3 className="m-0 mb-5 text-sm font-bold text-[var(--dash-ink)]">Engagement Index</h3>
          <DonutRing
            segments={[{ pct: engPct, color: '#EA580C' }]}
            centerValue={engScore}
            centerLabel="Total"
            size={140}
          />
          <p className="m-0 mt-4 text-center text-xs text-[var(--dash-muted)]">
            Based on visibility &amp; interaction
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <div className="rounded-2xl border border-[var(--dash-line)] bg-white p-6 shadow-sm">
          <h3 className="m-0 mb-1 text-sm font-bold text-[var(--dash-ink)]">FootFall Count</h3>
          <p className="m-0 mb-5 text-xs text-[var(--dash-muted)]">Total people passing by</p>

          <DonutRing
            segments={[{ pct: footfallPct, color: '#EA580C' }]}
            centerValue={d.footfallCount ?? '0'}
            centerLabel="Total"
            size={140}
          />
          <p className="m-0 mt-4 text-center text-xs font-semibold text-[var(--dash-ink)]">
            Total People : {d.footfallCount ?? '0'}
          </p>
        </div>
        <div className="rounded-2xl border border-[var(--dash-line)] bg-white p-6 shadow-sm">
          <h3 className="m-0 mb-5 text-sm font-bold text-[var(--dash-ink)]">Age Distribution</h3>

          {ageQ.isPending ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-[var(--dash-brand)]" />
            </div>
          ) : ageData.length === 0 ? (
            <p className="py-10 text-center text-sm italic text-[var(--dash-muted)]">
              No age distribution data available yet.
            </p>
          ) : (
            <div className="flex flex-col gap-3.5">
              {ageData.map((item, i) => {
                const pct = ageTotal > 0 ? ((item.count || 0) / ageTotal) * 100 : 0;
                const barColor = AGE_COLORS[item.range || ''] || '#3B82F6';
                return (
                  <div key={item.range || i} className="flex items-center gap-3">
                    <span className="w-12 shrink-0 text-sm font-semibold text-[var(--dash-ink)]">
                      {item.range}
                    </span>
                    <div className="relative flex-1 h-3.5 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="absolute inset-y-0 left-0 rounded-full transition-all duration-700"
                        style={{
                          width: `${pct}%`,
                          backgroundColor: barColor,
                          minWidth: (item.count || 0) > 0 ? '8px' : '0px',
                        }}
                      />
                    </div>
                    <span className="min-w-[48px] shrink-0 text-right text-sm font-semibold text-[var(--dash-muted)]">
                      {(item.count || 0).toLocaleString()}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-[var(--dash-line)] bg-white p-6 shadow-sm">
          <h3 className="m-0 mb-5 text-sm font-bold text-[var(--dash-ink)]">Vehicle Distribution</h3>
          <div className="flex items-start gap-8 sm:items-center">
            <DonutRing
              className="shrink-0"
              size={148}
              segments={vehicles.map((item) => ({
                pct: vehicleTotal > 0 ? (item.count / vehicleTotal) * 100 : 0,
                color: item.color,
              }))}
              centerValue={vehicleTotal}
              centerLabel="Total"
            />
            <div className="flex min-w-0 flex-1 flex-col gap-2.5">
              {vehicles.map((item) => (
                <div key={item.key} className="flex items-center justify-between gap-4">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="truncate text-sm text-[var(--dash-muted)]">{item.label}</span>
                  </div>
                  <span className="text-sm font-semibold text-[var(--dash-ink)]">
                    {item.count.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-[var(--dash-line)] bg-white p-6 shadow-sm">
          <h3 className="m-0 mb-5 text-sm font-bold text-[var(--dash-ink)]">Audience Count</h3>
          <div className="flex items-start gap-8 sm:items-center">
            <DonutRing
              className="shrink-0"
              size={148}
              segments={[
                { pct: malePct, color: '#2DD4BF' },
                { pct: femalePct, color: '#FB7185' },
              ]}
              centerValue={peopleCount}
              centerLabel="Total"
            />
            <div className="flex min-w-0 flex-1 flex-col gap-3">
              {audienceItems.map((item) => (
                <div key={item.key} className="flex items-center justify-between gap-4">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="truncate text-sm text-[var(--dash-muted)]">{item.label}</span>
                  </div>
                  <span className="text-sm font-semibold text-[var(--dash-ink)]">
                    {item.count.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
